import Layout from "../../components/Layout";
import { ArrowLeft } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import useCounter from "../../hooks/useCounter";
import ProjectTOC from "../../components/ProjectTOC";
import ComparisonBlock from "../../components/ComparisonBlock";
import ProjectNav from "../../components/ProjectNav";

export default function AIEducation() {
  useScrollReveal();

  const promptCount = useCounter(25);
  const accuracyGain = useCounter(4);
  const latencyLow = useCounter(1.2);
  const latencyHigh = useCounter(3.2);

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "architecture", label: "Architecture" },
    { id: "contributions", label: "Contributions" },
    { id: "pipeline", label: "Training Pipeline" },
    { id: "evaluation", label: "Evaluation" },
    { id: "diagram", label: "System Diagram" },
    { id: "compare", label: "Model Comparison" },
    { id: "download", label: "Download" },
  ];

  return (
    <Layout>
      {/* Sticky TOC */}
      <ProjectTOC sections={sections} />

      <div className="max-w-4xl mx-auto py-20 px-6 space-y-24 font-inter">

        {/* Back */}
        <a
          href="/projects"
          className="inline-flex items-center gap-2 text-sm opacity-60 hover:opacity-100 transition"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </a>

        {/* Hero */}
        <header className="space-y-6 reveal">
          <h1 className="text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1]">
            AI for Education in <br /> Developing Nations
          </h1>

          <p className="text-lg md:text-xl opacity-70 max-w-2xl">
            A fine-tuned GPT model trained on{" "}
            <span className="highlight-word">IB-aligned datasets</span> to deliver
            scalable exam-ready learning support for regions facing{" "}
            <span className="highlight-word">teacher shortages</span>.
          </p>

          <p className="text-sm uppercase tracking-[0.25em] opacity-50">
            Research • AI Model • Fine-Tuning
          </p>
        </header>

        {/* Metrics */}
        <section className="grid md:grid-cols-3 gap-10 reveal">
          {[
            { label: "Prompt Variations", value: `${promptCount}+` },
            { label: "Accuracy Gain", value: `${accuracyGain}.3×` },
            { label: "Response Time", value: `${latencyLow}–${latencyHigh}s` },
          ].map((item) => (
            <div
              key={item.label}
              className="metric rounded-3xl border border-black/10 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-xl p-10 text-center shadow-sm"
            >
              <p className="text-5xl md:text-6xl font-semibold mb-3 tracking-tight">
                {item.value}
              </p>
              <p className="text-sm uppercase tracking-wide opacity-70">
                {item.label}
              </p>
            </div>
          ))}
        </section>

        <div id="overview" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        {/* OVERVIEW */}
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Overview</h2>
          <p className="text-lg opacity-80 leading-relaxed">
            This project explores whether a{" "}
            <span className="highlight-word">fine-tuned GPT model</span> can
            provide exam-aligned support for students in{" "}
            <span className="highlight-word">developing nations</span>.
            Training data includes:
            <span className="highlight-word"> markschemes, command terms, structured datasets,</span>{" "}
            and curated question banks.
          </p>
        </section>

        {/* ARCHITECTURE */}
        <div id="architecture" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="space-y-8 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Technical Architecture</h2>

          <ul className="list-disc ml-6 space-y-4 text-lg opacity-80">
            <li>
              <span className="font-semibold highlight-word">Data Ingestion Layer:</span>
              CSV → JSON → JSONL transformer with schema checks + command-term tagging.
            </li>
            <li>
              <span className="font-semibold highlight-word">Fine-Tuning Layer:</span>
              Supervised training runs with temperature control + structured outputs.
            </li>
            <li>
              <span className="font-semibold highlight-word">Evaluation Engine:</span>
              Prompt-variation generator, key-term scoring, structural similarity metrics.
            </li>
          </ul>
        </section>

        {/* CONTRIBUTIONS */}
        <div id="contributions" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="rounded-3xl p-10 bg-white/30 dark:bg-white/5 backdrop-blur-xl border border-black/10 dark:border-white/10 space-y-8 reveal">
          <h3 className="text-3xl font-semibold">My Technical Contributions</h3>
          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80">
            <li>Designed complete dataset refinement pipeline</li>
            <li>Built JSONL training corpus with strict schema</li>
            <li>Configured OpenAI fine-tuning workloads</li>
            <li>Developed prompt-complexity evaluation suite</li>
            <li>Benchmarked outputs vs GPT-4o</li>
          </ul>
        </section>

        {/* TRAINING PIPELINE */}
        <div id="pipeline" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Training Pipeline</h2>

          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80">
            <li>Tokenization + normalization</li>
            <li>Command-term emphasis (Define, Analyse, Evaluate)</li>
            <li>Iterative supervised finetuning cycles</li>
            <li>Loss curve tracking and output pattern checks</li>
          </ul>
        </section>

        {/* EVALUATION */}
        <div id="evaluation" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Evaluation Methodology</h2>
          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80">
            <li>25+ prompt phrasings to test robustness</li>
            <li>Key-term frequency scoring</li>
            <li>IB-style structural similarity analysis</li>
            <li>Latency + token efficiency testing</li>
          </ul>
        </section>

        {/* SYSTEM DIAGRAM */}
        <div id="diagram" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">System Diagram</h2>

          <div
            className="p-6 rounded-3xl bg-white/20 dark:bg-white/5 
                       backdrop-blur-xl border border-black/10 
                       dark:border-white/10 font-mono text-sm 
                       whitespace-pre overflow-x-auto reveal"
          >
{`
DATASET → JSON TRANSFORM → JSONL → FINE-TUNE → EVALUATION

[Notes]   [Filtering]   [Schema]   [OpenAI FT]   [Benchmarks]
`}
          </div>
        </section>

        {/* COMPARISON BLOCK */}
        <div id="compare" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Model Comparison</h2>

          <ComparisonBlock
            aTitle="Fine-Tuned Model"
            bTitle="GPT-4o"
            aContent={
              <p className="text-lg opacity-80">
                • Strong IB alignment<br />
                • High key-term density<br />
                • Structured exam-style output<br />
                • Shorter, more concise responses
              </p>
            }
            bContent={
              <p className="text-lg opacity-80">
                • More creative but less exam-specific<br />
                • Weaker IB command-term accuracy<br />
                • Longer, less concise responses
              </p>
            }
          />
        </section>

        {/* DOWNLOAD */}
        <div id="download" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />
        <section className="reveal">
          <a
            href="/ai-education.pdf"
            className="text-lg underline opacity-70 hover:opacity-100 transition"
            download
          >
            Download full research (PDF)
          </a>
        </section>

        {/* NEXT / PREVIOUS PROJECT */}
        <ProjectNav
          prev={null}
          next={{ href: "/projects/ai-manufacturing-morex", label: "MOREX Automation" }}
        />

      </div>
    </Layout>
  );
}
